import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import Info from '@mui/icons-material/InfoOutline';
import {
  Button,
  Divider,
  FormLabel,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { formValidationSchema, type RegisterEmployerFormData } from './schema';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { AutocompleteStatesAndCities } from '@/components/partial/autocomplete-states-cities';
import { ContentBox } from '@/components/shared/content-box';
import { MaskedInput } from '@/components/shared/masked-input';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { useNotification } from '@/context/notification';
import { useRegisterEmployer } from '@/data/employees';

export const RegisterEmployer = () => {
  const navigate = useNavigate();
  const { toast } = useNotification();
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const { companyId } = useParams();
  const { control, handleSubmit } = useForm<RegisterEmployerFormData>({
    resolver: zodResolver(formValidationSchema),
    defaultValues: {
      email: '',
      cpf: '',
      name: '',
      is_admin: false,
    },
  });
  const { mutate: registerEmployer, isPending } = useRegisterEmployer();

  const handleRegisterEmployer = (data: RegisterEmployerFormData) => {
    registerEmployer(
      {
        data: {
          ...data,
          city: selectedCity,
          state: selectedState,
          email: data.email || '',
          cpf: data.cpf || '',
          company_id: companyId || '',
          type_registration: data.email ? 'email' : 'cpf',
        },
      },
      {
        onSuccess: ({ message }) => {
          toast({
            content: message || 'Colaborador adicionado com sucesso!',
            type: 'success',
          });
          navigate('/empresa/gestao-de-colaboradores');
        },
        onError: (error) => {
          toast({
            title: 'Erro ao adicionar colaborador',
            content:
              (error as AxiosError<{ error: string }>)?.response?.data
                ?.error ||
              'Entre em contato com o suporte ou tente novamente.',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <PageContainer>
      <PageHeader
        backNavigationText="Lista de colaboradores"
        handleBackNavigation={() =>
          navigate('/empresa/gestao-de-colaboradores')
        }
      />
      <ContentBox>
        <Grid container spacing={2} mb={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="h2">Adicionar colaborador</Typography>
          </Grid>
        </Grid>

        <Stack mb={2}>
          <FormLabel>Nome Completo*</FormLabel>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={!!error}
                placeholder="Insira o nome completo do colaborador"
                helperText={error?.message || ' '}
              />
            )}
          />
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel>E-mail</FormLabel>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  helperText={error?.message || ' '}
                  placeholder="colaborador@nomeempresa.com"
                />
              )}
            />
          </Stack>

          <Stack flex={1}>
            <FormLabel>CPF</FormLabel>
            <Controller
              control={control}
              name="cpf"
              render={({ field, fieldState: { error } }) => (
                <MaskedInput
                  {...field}
                  maskType="cpf"
                  error={!!error}
                  placeholder="123.456.789-00"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1} my={3}>
          <Typography
            fontWeight={fontWeights.fontWeightSemibold}
            variant="h3"
            color="primary.dark"
          >
            Atributos (opcional)
          </Typography>
          <Tooltip title="Preencher os campos opcionais permite que eles se tornem filtro no relatório de colaboradores.">
            <Info fontSize="small" />
          </Tooltip>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel
              id="internal_employee_id-label"
              htmlFor="internal_employee_id"
            >
              ID interno
            </FormLabel>
            <Controller
              control={control}
              name="internal_employee_id"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="internal_employee_id"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: número de matrícula, chave interna"
                  helperText={error?.message || ' '}
                  aria-labelledby="internal_employee_id-label"
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel id="type_collaborator-label" htmlFor="type_collaborator">
              Tipo de colaborador
            </FormLabel>
            <Controller
              control={control}
              name="type_collaborator"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="type_collaborator"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: CLT, externo, prestador de serviço"
                  helperText={error?.message || ' '}
                  aria-labelledby="type_collaborator-label"
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel id="position_company-label" htmlFor="position_company">
              Cargo
            </FormLabel>
            <Controller
              control={control}
              name="position_company"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="position_company"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: analista, coordenador"
                  helperText={error?.message || ' '}
                  aria-labelledby="position_company-label"
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel id="team-label" htmlFor="team">
              Equipe
            </FormLabel>
            <Controller
              control={control}
              name="team"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="team"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: atração de talentos, desenvolvimento"
                  helperText={error?.message || ' '}
                  aria-labelledby="team-label"
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel id="department-label" htmlFor="department">
              Departamento
            </FormLabel>
            <Controller
              control={control}
              name="department"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="department"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: recursos humanos, marketing"
                  helperText={error?.message || ' '}
                  aria-labelledby="department-label"
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel id="cost_center-label" htmlFor="cost_center">
              Centro de custo
            </FormLabel>
            <Controller
              control={control}
              name="cost_center"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="cost_center"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: produtos digitais"
                  helperText={error?.message || ' '}
                  aria-labelledby="cost_center-label"
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel id="manager_email-label" htmlFor="manager_email">
              Gestor
            </FormLabel>
            <Controller
              control={control}
              name="manager_email"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="manager_email"
                  type="email"
                  error={!!error}
                  fullWidth
                  placeholder="Insira o email"
                  helperText={error?.message || ' '}
                  aria-labelledby="manager_email-label"
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel
              id="company-label"
              htmlFor="company"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              Empresa
              <Tooltip title="Campo para holding que possuem diversas empresas.">
                <Info fontSize="small" aria-hidden="true" />
              </Tooltip>
            </FormLabel>
            <Controller
              control={control}
              name="company"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="company"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: nome da empresa"
                  helperText={error?.message || ' '}
                  aria-labelledby="company-label"
                />
              )}
            />
          </Stack>
        </Stack>
        <AutocompleteStatesAndCities
          onStateAndCityChange={(state, city) => {
            setSelectedState(state);
            setSelectedCity(city);
          }}
          initialState={selectedState}
          initialCity={selectedCity}
        />
        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel id="region-label" htmlFor="region">
              Região
            </FormLabel>
            <Controller
              control={control}
              name="region"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="region"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: filial, região de atuação"
                  helperText={error?.message || ' '}
                  aria-labelledby="region-label"
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel id="building-label" htmlFor="building">
              Prédio
            </FormLabel>
            <Controller
              control={control}
              name="building"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="building"
                  error={!!error}
                  fullWidth
                  placeholder="ex.: nome do edifício, campus ou andar"
                  helperText={error?.message || ' '}
                  aria-labelledby="building-label"
                />
              )}
            />
          </Stack>
        </Stack>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/empresa/gestao-de-colaboradores')}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            loading={isPending}
            onClick={handleSubmit(handleRegisterEmployer)}
          >
            Adicionar colaborador
          </Button>
        </Stack>
      </ContentBox>
    </PageContainer>
  );
};
